/**
 * Who holds the forms browser of a mission's form workshop (PLAN-ATELIER-OTTO § 16), as the
 * server reads it (`get_workshop_control`). The state is derived in SQL (`v_workshop_presence`):
 * the card only turns it into words and offers the one gesture that makes sense.
 */
import { screenErrorMessage } from './workshopScreen';

export type ControlState = 'idle' | 'agent' | 'switching_to_human' | 'human' | 'paused';

export interface WorkshopControl {
  workshop_id: string;
  thread_id: string;
  workshop_status: string;
  attempt: number;
  deadline_at: string | null;
  state: ControlState;
  since: string | null;
  requested_mode: 'agent' | 'human';
  applied_mode: 'agent' | 'human';
  paused_seconds: number;
  /** The hand was asked by the current user. */
  mine: boolean;
}

export interface WorkshopControlRead {
  /** Public origin of the screen gate (Tailscale Funnel), null until the setting exists. */
  screen_url: string | null;
  /** Named decider: may take and give back the hand. */
  can_control: boolean;
  /** May act in the browser now: named decider, and no workshop holds it or the hand is theirs (SQL). */
  screen_controls: boolean;
  /**
   * The browser works for another person's mission (lot L7e): nothing of it is said, no hand to take, and its screen
   * opens to that person only. Absent from a server of before.
   */
  held_by_another?: boolean;
  workshop: WorkshopControl | null;
}

/** What the card says instead of the screen while another person's mission holds the browser, or null. Pure. */
export function heldByAnotherNotice(read: Pick<WorkshopControlRead, 'held_by_another'> | null): string | null {
  return read?.held_by_another === true ? screenErrorMessage({ message: 'screen_held_by_another' }) : null;
}

export type ControlTone = 'otto' | 'human' | 'pending' | 'idle';

export interface ControlView {
  label: string;
  detail: string;
  tone: ControlTone;
  /** The gesture offered now, if the user may make it. */
  action: 'take' | 'release' | null;
}

/** Words and gesture for a state. Pure. */
export function controlView(read: WorkshopControlRead): ControlView | null {
  const w = read.workshop;
  if (!w) return null;
  const can = read.can_control;
  switch (w.state) {
    case 'agent':
      return {
        label: 'Otto pilote',
        detail: 'Otto remplit le formulaire dans le navigateur de la machine. Tu peux regarder sans rien interrompre.',
        tone: 'otto',
        action: can ? 'take' : null,
      };
    case 'switching_to_human':
      return {
        label: 'Prise de main…',
        detail: "La machine bascule (quelques secondes). Tant qu'elle n'a pas confirmé, Otto peut encore agir : ne tape rien.",
        tone: 'pending',
        action: can ? 'release' : null,
      };
    case 'human':
      return {
        label: w.mine ? 'Tu as la main' : 'Un humain a la main',
        detail: "Otto ne peut plus rien faire dans ce navigateur et ne voit rien de ce qui s'y passe. Son temps est suspendu (30 min au plus par prise).",
        tone: 'human',
        action: can ? 'release' : null,
      };
    case 'paused':
      return {
        label: 'En pause',
        detail: 'Main rendue : Otto reprend dès que la machine confirme, en relisant la page.',
        tone: 'pending',
        action: null,
      };
    case 'idle':
      return {
        label: 'Atelier terminé',
        detail: "Plus aucun atelier ne tient le navigateur : l'écran reste consultable.",
        tone: 'idle',
        action: null,
      };
  }
}

/** A state that moves on its own (the machine confirms within seconds): read it again soon. */
export function controlNeedsPolling(read: WorkshopControlRead | null): boolean {
  return Boolean(read?.workshop && read.workshop.state !== 'idle');
}

/** A server refusal in words. */
export function controlErrorMessage(message: string): string {
  if (message.includes('workshop_not_running')) return "L'atelier n'est plus en cours : il n'y a plus de main à prendre.";
  if (message.includes('named deciders')) return 'Réservé aux personnes nommées pour trancher les livrables.';
  if (message.includes('control_form_only')) return "Seul un atelier formulaire a un navigateur dont on peut prendre la main.";
  return "La bascule n'a pas été enregistrée. Réessaie.";
}
